import React, { PropsWithoutRef, useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Typography,
  Button,
  Modal,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockResetIcon from "@mui/icons-material/LockReset";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate } from "react-router-dom";
import {
  SIDEBAR_WIDTH,
  STORAGE_KEYS,
  getFromStorage,
  removeFromStorage,
  showError,
} from "../constants";

import "./Layout.scss";
import useAuth from "../hooks/useAuth";
import { getToken, resetAuth, setCredentials } from "../reducers/authSlice";
import { useAppDispatch, useAppSelector } from "../hooks/store";
import { useLazyGetUserQuery } from "../services/auth";
import { generateResponsiveStyle } from "../utils/ModalStyle";
import { connectWallet } from "../utils/connectWallet";

interface Props {
  handleSidebarToggle: () => void;
}

declare global {
  interface Window {
    ethereum?: any; // This declares the ethereum object on the window
  }
}
function Topbar({ handleSidebarToggle }: PropsWithoutRef<Props>) {
  const navigate = useNavigate();
  const user = useAuth();

  const dispatch = useAppDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [scroll, setScroll] = useState(false);
  const [address, setAddress] = useState("");
  const style = generateResponsiveStyle();
  const token = useAppSelector(getToken);
  const [getUser] = useLazyGetUserQuery();

  const getUserDetails = async () => {
    const token = getFromStorage(STORAGE_KEYS.token);
    try {
      const result = await getUser({}).unwrap();
      if (result?.statusCode === 200 && token) {
        dispatch(
          setCredentials({
            user: result?.data,
            token: token,
          })
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleLogout = () => {
    removeFromStorage(STORAGE_KEYS.token);
    dispatch(resetAuth());
    navigate("/", { replace: true });
  };

  useEffect(() => {
    const token = getFromStorage(STORAGE_KEYS.token);
    if (token) {
      dispatch(
        setCredentials({
          user: null,
          token: token,
        })
      );
    }
  }, []);

  useEffect(() => {
    if (window) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    const token = getFromStorage(STORAGE_KEYS.token);
    const userData = getFromStorage(STORAGE_KEYS.credentials);
    if (token) {
      dispatch(
        setCredentials({
          token: `${token}`,
          user: JSON.parse(`${userData}`),
        })
      );
    }
    getUserDetails();
  }, []);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    window.addEventListener("scroll", () => {
      setScroll(window.scrollY > 10);
    });
  }, []);

  // useEffect(() => {
  //   connectWallet();
  // }, []);

  // const connectWallet = async () => {
  //   console.log("web connect");
  //   if (window.ethereum) {
  //     console.log("detected");
  //     try {
  //       const accounts = await window.ethereum.request({
  //         method: "eth_requestAccounts",
  //       });
  //       if (accounts.length) {
  //         const body = {
  //           walletAddress: accounts[0],
  //         };
  //         console.log(accounts, body, "body");
  //         const token = getFromStorage("token");
  //       }
  //     } catch (err) {
  //       console.log(err);
  //     }
  //   } else {
  //     console.log("meta mask not detected");
  //     window.open("https://metamask.io/download/", "_blank");
  //   }
  // };
  const connectMetamask = async () => {
    console.log("web connect");
    if (window.ethereum) {
      console.log("detected");
      try {
        const accounts = await window.ethereum.request({
          method: "eth_requestAccounts",
        });
        console.log(accounts);
        const account = accounts[0];
        setAddress(account);
        return account;
      } catch {
        console.log("ERROR");
      }
    } else {
      showError("MetaMask not found. Please install MetaMask extension.");
      throw new Error("MetaMask not found. Please install MetaMask extension.");
    }
  };

  const handleConnectMetamask = async () => {
    try {
      connectMetamask();
      if (address) {
        showError("MetaMask already connected");
      } else {
        connectMetamask();
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Box className="container top_bar">
      <AppBar
        position="fixed"
        className="topbar"
        sx={{
          width: { lg: `calc(100% - ${SIDEBAR_WIDTH}px)` },
          ml: { lg: `${SIDEBAR_WIDTH}px` },
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <IconButton
            color="inherit"
            aria-label="open sidebar"
            edge="start"
            onClick={handleSidebarToggle}
          >
            <MenuIcon />
          </IconButton>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              justifyContent: "flex-end",
            }}
          >
            <figure onClick={handleConnectMetamask}>
              <img
                src="/static/images/metamask_img.svg"
                style={{ cursor: "pointer" }}
                alt=""
              />
            </figure>
            <Box>
              <IconButton
                className="topbar_user_btn"
                aria-controls={open ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
              >
                <img
                  src={
                    user?.profileImage
                      ? user?.profileImage
                      : "/static/images/user_placeholder.png"
                  }
                  alt=""
                />
                <span> {user?.fullName || "User"}</span>
                <KeyboardArrowDownIcon />
              </IconButton>
              <Menu
                className="userDropdown_menu"
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                    mt: 1.5,
                    "& .MuiAvatar-root": {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    "&:before": {
                      content: '""',
                      display: "block",
                      position: "absolute",
                      top: 0,
                      right: 10,
                      width: 10,
                      height: 10,
                      bgcolor: "background.paper",
                      transform: "translateY(-50%) rotate(45deg)",
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem
                  className="userDropdown_menu_list"
                  onClick={() => navigate("/profile")}
                >
                  <IconButton className="drpdwn_icns">
                    <PersonIcon />
                  </IconButton>
                  My Profile
                </MenuItem>
                <MenuItem
                  className="userDropdown_menu_list"
                  onClick={() => navigate("/changepassword")}
                >
                  <IconButton className="drpdwn_icns">
                    <LockResetIcon />
                  </IconButton>
                  Change Password
                </MenuItem>
                <MenuItem
                  className="userDropdown_menu_list"
                  onClick={() => setOpenModal(true)}
                >
                  <IconButton className="drpdwn_icns">
                    <LogoutIcon />
                  </IconButton>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="logout_mdl"
      >
        <Box sx={style}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            sx={{ textAlign: "center", fontSize: "20px", color: "white" }}
          >
            Are you sure, you want to logout?
          </Typography>
          <div className="flexdiv">
            <Button
              className="yesBtn"
              sx={{ color: "#252525", border: "1px solid #252525" }}
              onClick={() => {
                setOpenModal(false);
                handleLogout();
              }}
            >
              Yes
            </Button>

            <Button
              sx={{ color: "#252525", border: "1px solid #252525" }}
              onClick={() => setOpenModal(false)}
            >
              No
            </Button>
          </div>
        </Box>
      </Modal>
    </Box>
  );
}

export default Topbar;
